/**
 * Feature registry — aggregates every feature descriptor and exposes the subset
 * enabled for the current deployment + caller. `App` and `AppShell` build the
 * router and nav from this, so adding a feature is a one-line import here plus its
 * slice — never an edit to routing/nav control flow.
 */
import { alertsFeature } from "@/features/alerts";
import { analyticsFeature } from "@/features/analytics";
import { billingFeature } from "@/features/billing";
import { consumptionFeature } from "@/features/consumption";
import { dashboardFeature } from "@/features/dashboard";
import { localitiesFeature } from "@/features/localities";
import { nrwFeature } from "@/features/nrw";
import { sensorsFeature } from "@/features/sensors";
import { simulatorFeature } from "@/features/simulator";
import { glossaryFeature } from "@/features/glossary";
import { settingsFeature } from "@/features/settings";
import { syncMonitorFeature } from "@/features/sync_monitor";
import { workFeature } from "@/features/work";
import { experimentsFeature } from "@/features/experiments";
import { chatWidget } from "@/features/chat";
import type {
  FeatureDescriptor,
  ScopeControls,
  ShellWidgetDescriptor,
} from "@/features/types";
import type { MetaUser } from "@/lib/meta";

export const ALL_FEATURES: FeatureDescriptor[] = [
  dashboardFeature,
  consumptionFeature,
  localitiesFeature,
  sensorsFeature,
  alertsFeature,
  workFeature,
  analyticsFeature,
  nrwFeature,
  billingFeature,
  simulatorFeature,
  experimentsFeature,
  syncMonitorFeature,
  settingsFeature,
  glossaryFeature,
];

/**
 * Feature-owned surfaces the shell mounts outside the router (see
 * `ShellWidgetDescriptor`). Adding one is a one-line import here, exactly like a
 * routed feature — `AppShell` never names a widget.
 */
export const ALL_SHELL_WIDGETS: ShellWidgetDescriptor[] = [chatWidget];

/** Structural: both descriptor kinds gate on the same optional role list. */
function roleOk(entry: { roles?: string[] }, user: MetaUser | null): boolean {
  if (!entry.roles || entry.roles.length === 0) return true;
  if (!user) return false;
  if (user.roles.includes("admin")) return true;
  return entry.roles.some((r) => user.roles.includes(r));
}

/** Features enabled by both the backend flag map and the caller's roles. */
export function enabledFeatures(
  flags: Record<string, boolean>,
  user: MetaUser | null,
): FeatureDescriptor[] {
  return ALL_FEATURES.filter((f) => (flags[f.flag] ?? false) && roleOk(f, user));
}

/** Shell widgets enabled for this deployment + caller. */
export function enabledShellWidgets(
  flags: Record<string, boolean>,
  user: MetaUser | null,
): ShellWidgetDescriptor[] {
  return ALL_SHELL_WIDGETS.filter((w) => (flags[w.flag] ?? false) && roleOk(w, user));
}

/** Exact segment match, treating ":param" segments as single-segment wildcards. */
function pathMatches(pattern: string, pathname: string): boolean {
  const norm = (s: string) => s.replace(/^\/+|\/+$/g, "");
  const pp = norm(pattern).split("/").filter(Boolean);
  const xx = norm(pathname).split("/").filter(Boolean);
  if (pp.length !== xx.length) return false;
  return pp.every((seg, i) => seg.startsWith(":") || seg === xx[i]);
}

/** Static-segment count → prefer a static route ("sensors/registry") over a
 * dynamic one ("sensors/:id") when both match the same path. */
function specificity(pattern: string): number {
  return pattern.split("/").filter((s) => s && !s.startsWith(":")).length;
}

/**
 * Scope declaration for `pathname`, resolved without feature flags or a user.
 *
 * `ScopeProvider` needs a route's scope *rules* (notably `inheritLocality`) while
 * deriving the scope itself, which is upstream of auth — so this deliberately
 * scans every feature rather than only the enabled ones. A route belonging to a
 * disabled feature is unreachable anyway, so the wider scan changes nothing.
 * Use `activeScope` for rendering decisions, which must respect enablement.
 */
function resolveScope(
  pathname: string,
  features: FeatureDescriptor[],
): ScopeControls | null {
  let best: ScopeControls | null = null;
  let bestScore = -1;
  const consider = (pattern: string, scope: ScopeControls | undefined) => {
    if (!pathMatches(pattern, pathname)) return;
    const score = specificity(pattern);
    if (score > bestScore) {
      bestScore = score;
      best = scope ?? null;
    }
  };
  for (const f of features) {
    consider(f.index ? "/" : f.path, f.scope);
    for (const sub of f.subRoutes ?? [])
      consider("/" + sub.path.replace(/^\//, ""), sub.scope);
  }
  return best;
}

export function routeScope(pathname: string): ScopeControls | null {
  return resolveScope(pathname, ALL_FEATURES);
}

/**
 * Scope controls for the route at `pathname`, limited to features this user can
 * actually reach. Drives which global controls the header `ScopeBar` renders.
 */
export function activeScope(
  pathname: string,
  flags: Record<string, boolean>,
  user: MetaUser | null,
): ScopeControls | null {
  return resolveScope(pathname, enabledFeatures(flags, user));
}
